import React, { useState, useEffect } from "react"
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  LinearProgress,
  Switch,
  FormControlLabel,
} from "@mui/material"
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Download as DownloadIcon,
  CloudUpload as CloudUploadIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  VideoFile as VideoIcon,
  Image as ImageIcon,
  AudioFile as AudioIcon,
  Sync as SyncIcon,
} from "@mui/icons-material"
import { useSnackbar } from "notistack"
import {
  getAiAgentKnowledgeBaseList,
  addAiAgentKnowledgeBase,
  deleteAiAgentKnowledgeBase,
  uploadAiAgentKnowledgeBaseFile,
  uploadAiAgentKnowledgeBaseFileChunked,
  cancelAiAgentKnowledgeBaseUpload,
  downloadAiAgentKnowledgeBaseFile,
  bulkOperationAiAgentKnowledgeBase,
  reprocessFailedAiAgentKnowledgeBase,
  syncExternalAiAgentKnowledgeBaseSource,
  syncExternalAiAgentKnowledgeBaseSources,
  startAiAgentKnowledgeBaseOAuth,
  listAiAgentKnowledgeBaseOAuthCredentials,
  searchAiAgentKnowledgeBase,
  ragSearchAiAgentKnowledgeBase,
} from "services/aiAgent"
import { verifyResponse } from "utils/httpRequest"
import { ACCEPT_FILE_EXT } from "constants/global"
import { findMediaInfoByFileName } from "utils/media"
import RichTextEditor from "components/common/RichTextEditor"

const KNOWLEDGE_SOURCE_MODES = {
  MANUAL: "manual",
  EXTERNAL_SYNC: "external_sync",
}

const EXTERNAL_SOURCE_PROVIDERS = [
  { value: "google_drive", label: "Google Drive folder(s)" },
  { value: "notion", label: "Notion workspace/page(s)" },
  { value: "obsidian", label: "Obsidian vault folder(s)" },
  { value: "website", label: "Website / sitemap" },
  { value: "other", label: "Other knowledge base" },
]

const SYNC_FREQUENCY_OPTIONS = [
  { value: "realtime", label: "Realtime / webhook when available" },
  { value: "hourly", label: "Hourly scan" },
  { value: "daily", label: "Daily scan" },
  { value: "manual", label: "Manual sync only" },
]

const parseMultilineList = (value) =>
  String(value || "")
    .split(/\r?\n|,/)
    .map((item) => item.trim())
    .filter(Boolean)

const KnowledgeBasePanel = ({ agentId, access, onCountChange, refreshSignal }) => {
  const [knowledgeBase, setKnowledgeBase] = useState([])
  const [loading, setLoading] = useState(false)
  const [addKnowledgeDialog, setAddKnowledgeDialog] = useState(false)
  const [deleteKnowledgeDialog, setDeleteKnowledgeDialog] = useState(false)
  const [viewKnowledgeDialog, setViewKnowledgeDialog] = useState(false)
  const [knowledgeToDelete, setKnowledgeToDelete] = useState(null)
  const [knowledgeToView, setKnowledgeToView] = useState(null)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [selectedFile, setSelectedFile] = useState(null)
  const [dragActive, setDragActive] = useState(false)
  const [isChunkedUpload, setIsChunkedUpload] = useState(false)
  const [currentChunk, setCurrentChunk] = useState(0)
  const [totalChunks, setTotalChunks] = useState(0)
  const [uploadId, setUploadId] = useState(null)
  const [canCancelUpload, setCanCancelUpload] = useState(false)
  const [selectedItems, setSelectedItems] = useState([])
  const [searchKeyword, setSearchKeyword] = useState('')
  const [searchContentType, setSearchContentType] = useState('')
  const [searchStatus, setSearchStatus] = useState('')
  const [ragSearchDialog, setRagSearchDialog] = useState(false)
  const [ragSearchQuery, setRagSearchQuery] = useState('')
  const [ragSearchResults, setRagSearchResults] = useState([])
  const [oauthCredentials, setOauthCredentials] = useState([])
  const [oauthConnecting, setOauthConnecting] = useState(false)
  const [newKnowledge, setNewKnowledge] = useState({
    sourceMode: KNOWLEDGE_SOURCE_MODES.MANUAL,
    contentTitle: "",
    contentDescription: "",
    filePath: "",
    fileSize: "",
    mimeType: "",
    contentText: "",
    sourceProvider: "google_drive",
    sourceUrls: "",
    syncFrequency: "hourly",
    recursiveSync: true,
    realtimeSync: true,
    includePatterns: "",
    excludePatterns: "",
    credentialId: "",
    credentialDisplayName: "",
  })
  const { enqueueSnackbar } = useSnackbar()
  const dialogPaperSx = {
    width: "min(1020px, calc(100vw - 28px))",
    maxWidth: "none",
    borderRadius: "16px",
    border: "1px solid #dce6f3",
    background: "linear-gradient(180deg, #ffffff 0%, #f7faff 100%)",
    boxShadow: "0 24px 48px rgba(15, 23, 42, 0.2)",
  }
  const dialogActionBtnSx = {
    textTransform: "none",
    borderRadius: "9px",
    fontWeight: 700,
  }

  useEffect(() => {
    if (agentId === undefined || agentId === null) return
    loadKnowledgeBase()
  }, [agentId, refreshSignal])

  useEffect(() => {
    if (!addKnowledgeDialog || newKnowledge.sourceMode !== KNOWLEDGE_SOURCE_MODES.EXTERNAL_SYNC) return
    let mounted = true
    ;(async () => {
      try {
        const response = await listAiAgentKnowledgeBaseOAuthCredentials(newKnowledge.sourceProvider)
        verifyResponse(response)
        const list = response?.data || response || []
        if (mounted) setOauthCredentials(Array.isArray(list) ? list : [])
      } catch (error) {
        if (mounted) setOauthCredentials([])
      }
    })()
    return () => {
      mounted = false
    }
  }, [addKnowledgeDialog, newKnowledge.sourceMode, newKnowledge.sourceProvider])

  const loadKnowledgeBase = async () => {
    if (agentId === undefined || agentId === null) return
    setLoading(true)
    try {
      const response = await getAiAgentKnowledgeBaseList({
        page: 1,
        pageSize: 100,
        agentId: agentId,
      })
      verifyResponse(response)
      const list = response.data?.list || []
      setKnowledgeBase(list)
      onCountChange?.(list.length)
    } catch (error) {
      console.error("Error loading knowledge base:", error)
      enqueueSnackbar("Error loading knowledge base", { variant: "error" })
    } finally {
      setLoading(false)
    }
  }

  const handleAddKnowledge = async () => {
    // Validate required fields
    if (!newKnowledge.contentTitle.trim()) {
      enqueueSnackbar("Content title is required", { variant: "error" })
      return
    }

    const isExternalSyncSource = newKnowledge.sourceMode === KNOWLEDGE_SOURCE_MODES.EXTERNAL_SYNC

    if (isExternalSyncSource) {
      const sourceUrls = parseMultilineList(newKnowledge.sourceUrls)
      if (!newKnowledge.sourceProvider) {
        enqueueSnackbar("Source provider is required", { variant: "error" })
        return
      }
      if (sourceUrls.length === 0) {
        enqueueSnackbar("Add at least one folder, page, vault, or knowledge base URL", { variant: "error" })
        return
      }
      if (newKnowledge.sourceProvider === "google_drive" && !newKnowledge.credentialId) {
        enqueueSnackbar("Connect and select a Google Drive account before saving this source", { variant: "error" })
        return
      }

      setLoading(true)
      setUploadProgress(0)

      try {
        const providerLabel =
          EXTERNAL_SOURCE_PROVIDERS.find((provider) => provider.value === newKnowledge.sourceProvider)?.label ||
          newKnowledge.sourceProvider
        const submitData = {
          agentId: agentId,
          contentType: "external_source",
          contentTitle: newKnowledge.contentTitle,
          contentDescription: newKnowledge.contentDescription,
          contentUrl: sourceUrls[0],
          contentText: "",
          fileSize: 0,
          mimeType: "application/x-rag-sync-source",
          metadata: {
            ragSync: {
              enabled: true,
              provider: newKnowledge.sourceProvider,
              providerLabel,
              sourceUrls,
              syncFrequency: newKnowledge.syncFrequency,
              recursive: !!newKnowledge.recursiveSync,
              realtime: !!newKnowledge.realtimeSync,
              includePatterns: parseMultilineList(newKnowledge.includePatterns),
              excludePatterns: parseMultilineList(newKnowledge.excludePatterns),
              credentialId: newKnowledge.credentialId ? Number(newKnowledge.credentialId) : null,
              credentialDisplayName: newKnowledge.credentialDisplayName || null,
              configuredAt: new Date().toISOString(),
              status: "configured",
            },
          },
        }

        const response = await addAiAgentKnowledgeBase(submitData)
        verifyResponse(response)
        enqueueSnackbar("External sync source configured successfully", { variant: "success" })
        handleCloseDialog()
        loadKnowledgeBase()
      } catch (error) {
        console.error("Error configuring external sync source:", error)
        const errorMessage = error.response?.data?.message || "Error configuring external sync source"
        enqueueSnackbar(errorMessage, { variant: "error" })
      } finally {
        setLoading(false)
        setUploadProgress(0)
      }
      return
    }

    // Determine content type based on what's provided
    let contentType = "text" // default

    if (selectedFile) {
      // File-based content - determine type from file extension
      const mediaInfo = findMediaInfoByFileName(selectedFile.name)
      contentType = mediaInfo.type
    } else if (newKnowledge.contentText.trim()) {
      contentType = "text"
    } else {
      enqueueSnackbar("Please provide either a file or text content", { variant: "error" })
      return
    }

    // Validate based on determined content type
    if (contentType === "text") {
      if (!newKnowledge.contentText.trim()) {
        enqueueSnackbar("Content text is required for text type items", { variant: "error" })
        return
      }
    }

    setLoading(true)
    setUploadProgress(0)

    try {
      let response

      if (selectedFile) {
        // Handle file upload - determine content type from file extension
        const mediaInfo = findMediaInfoByFileName(selectedFile.name)
        const fileExtension = (selectedFile.name || "").split(".").pop()?.toLowerCase() || ""
        // Backend supports .md under ContentType.TEXT; treat Markdown as text for best RAG ingestion.
        const resolvedContentType = fileExtension === "md" ? "text" : mediaInfo.type
        const metadata = {
          contentTitle: newKnowledge.contentTitle,
          contentDescription: newKnowledge.contentDescription,
          contentType: resolvedContentType, // Automatically determine from file extension (+ markdown → text)
          processImmediately: true,
        }

        // Check if file is large enough for chunked upload (25MB threshold)
        const CHUNKING_THRESHOLD = 25 * 1024 * 1024 // 25MB
        const useChunkedUpload = selectedFile.size >= CHUNKING_THRESHOLD

        if (useChunkedUpload) {
          setIsChunkedUpload(true)
          setCanCancelUpload(true)

          response = await uploadAiAgentKnowledgeBaseFileChunked(selectedFile, agentId, metadata, {
            onProgress: (chunkIndex, chunkProgress, totalChunks) => {
              // Calculate overall progress
              const chunkWeight = 100 / totalChunks
              const completedWeight = (chunkIndex - 1) * chunkWeight
              const currentWeight = (chunkProgress / 100) * chunkWeight
              const overallProgress = Math.round(completedWeight + currentWeight)

              setUploadProgress(overallProgress)
              setCurrentChunk(chunkIndex)
              setTotalChunks(totalChunks)
            },
            onChunkComplete: (completedChunks, totalChunks, chunkResponse) => {
              setCurrentChunk(completedChunks)
              setTotalChunks(totalChunks)
            },
            onError: (error, chunkNumber, totalChunks) => {
              console.error(`Error uploading chunk ${chunkNumber}/${totalChunks}:`, error)
              enqueueSnackbar(`Error uploading chunk ${chunkNumber}/${totalChunks}: ${error.message}`, { variant: "error" })
            },
            onCancel: (uploadId) => {
              setUploadId(uploadId)
            }
          })
        } else {
          // Use regular upload for smaller files
          response = await uploadAiAgentKnowledgeBaseFile(selectedFile, agentId, {
            ...metadata,
            onProgress: (progress) => setUploadProgress(progress)
          })
        }
      } else {
        // Handle text/URL content
        const submitData = {
          ...newKnowledge,
          contentType: contentType, // Use the determined content type
          agentId: agentId,
          fileSize: parseInt(newKnowledge.fileSize) || 0,
        }
        response = await addAiAgentKnowledgeBase(submitData)
      }

      verifyResponse(response)

      // Handle different response formats
      if (response.success !== undefined) {
        // Chunked upload response format
        if (response.success && response.isComplete) {
          enqueueSnackbar("File uploaded and processing started successfully", { variant: "success" })
        } else if (response.success && !response.isComplete) {
          enqueueSnackbar(`Chunk ${response.chunkIndex + 1}/${response.totalChunks} uploaded successfully`, { variant: "info" })
        } else {
          enqueueSnackbar("Upload completed successfully", { variant: "success" })
        }
      } else {
        // Regular upload response format
        enqueueSnackbar("Knowledge base item added successfully", { variant: "success" })
      }

      handleCloseDialog()
      loadKnowledgeBase()
    } catch (error) {
      console.error("Error adding knowledge base item:", error)
      const errorMessage = error.response?.data?.message || "Error adding knowledge base item"
      enqueueSnackbar(errorMessage, { variant: "error" })
    } finally {
      setLoading(false)
      setUploadProgress(0)
    }
  }

  const handleDeleteKnowledge = async () => {
    if (!knowledgeToDelete) return
    setLoading(true)
    try {
      const response = await deleteAiAgentKnowledgeBase(knowledgeToDelete.id)
      verifyResponse(response)
      enqueueSnackbar("Knowledge base item deleted successfully", { variant: "success" })
      setDeleteKnowledgeDialog(false)
      setKnowledgeToDelete(null)
      loadKnowledgeBase()
    } catch (error) {
      console.error("Error deleting knowledge base item:", error)
      enqueueSnackbar("Error deleting knowledge base item", { variant: "error" })
    } finally {
      setLoading(false)
    }
  }

  const handleViewKnowledge = (item) => {
    setKnowledgeToView(item)
    setViewKnowledgeDialog(true)
  }

  const handleFileSelect = (file) => {
    if (!file) return

    // Validate file size (e.g., 100MB limit)
    const maxSize = 100 * 1024 * 1024 // 100MB
    if (file.size > maxSize) {
      enqueueSnackbar("File size exceeds 100MB limit", { variant: "error" })
      return
    }

    // Validate file extension
    const fileName = file.name
    const fileExtension = fileName.split('.').pop()?.toLowerCase()

    if (!ACCEPT_FILE_EXT.includes(fileExtension)) {
      enqueueSnackbar(`File type .${fileExtension} is not supported. Please use: ${ACCEPT_FILE_EXT.join(', ')}`, { variant: "error" })
      return
    }

    // Get media info for better display
    const mediaInfo = findMediaInfoByFileName(fileName)

    setSelectedFile(file)
    setNewKnowledge({
      ...newKnowledge,
      contentTitle: file.name,
      mimeType: file.type,
      fileSize: file.size.toString(),
    })
  }

  const handleDrag = (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true)
    } else if (e.type === "dragleave") {
      setDragActive(false)
    }
  }

  const handleDrop = (e) => {
    e.preventDefault()
    e.stopPropagation()
    setDragActive(false)

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileSelect(e.dataTransfer.files[0])
    }
  }

  const handleFileInput = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFileSelect(e.target.files[0])
    }
  }

  const removeSelectedFile = () => {
    if (selectedFile) {
      // Clean up object URL to prevent memory leaks
      URL.revokeObjectURL(selectedFile)
    }
    setSelectedFile(null)
    setNewKnowledge({
      ...newKnowledge,
      contentTitle: "",
      mimeType: "",
      fileSize: "",
    })
  }

  const cancelUpload = async () => {
    if (uploadId && canCancelUpload) {
      try {
        const response = await cancelAiAgentKnowledgeBaseUpload(uploadId)
        verifyResponse(response)

        // Handle different response formats
        if (response.success !== undefined) {
          enqueueSnackbar(response.message || "Upload canceled successfully", { variant: "info" })
        } else {
          enqueueSnackbar("Upload canceled successfully", { variant: "info" })
        }
      } catch (error) {
        console.error("Error canceling upload:", error)
        const errorMessage = error.response?.data?.message || "Error canceling upload"
        enqueueSnackbar(errorMessage, { variant: "error" })
      }
    }

    // Reset upload state
    setIsChunkedUpload(false)
    setCurrentChunk(0)
    setTotalChunks(0)
    setUploadId(null)
    setCanCancelUpload(false)
    setUploadProgress(0)
  }

  const resetForm = () => {
    if (selectedFile) {
      // Clean up object URL to prevent memory leaks
      URL.revokeObjectURL(selectedFile)
    }
    setNewKnowledge({
      sourceMode: KNOWLEDGE_SOURCE_MODES.MANUAL,
      contentTitle: "",
      contentDescription: "",
      filePath: "",
      fileSize: "",
      mimeType: "",
      contentText: "",
      sourceProvider: "google_drive",
      sourceUrls: "",
      syncFrequency: "hourly",
      recursiveSync: true,
      realtimeSync: true,
      includePatterns: "",
      excludePatterns: "",
      credentialId: "",
      credentialDisplayName: "",
    })
    setSelectedFile(null)
    setUploadProgress(0)
    setIsChunkedUpload(false)
    setCurrentChunk(0)
    setTotalChunks(0)
    setUploadId(null)
    setCanCancelUpload(false)
  }

  // Bulk operations
  const handleSelectItem = (itemId) => {
    setSelectedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    )
  }

  const handleSelectAll = () => {
    if (selectedItems.length === knowledgeBase.length) {
      setSelectedItems([])
    } else {
      setSelectedItems(knowledgeBase.map(item => item.id))
    }
  }

  const handleBulkOperation = async (operation) => {
    if (selectedItems.length === 0) {
      enqueueSnackbar("Please select items to perform bulk operation", { variant: "warning" })
      return
    }

    setLoading(true)
    try {
      const response = await bulkOperationAiAgentKnowledgeBase(operation, selectedItems, agentId)
      verifyResponse(response)
      enqueueSnackbar(`Bulk ${operation} completed successfully`, { variant: "success" })
      setSelectedItems([])
      loadKnowledgeBase()
    } catch (error) {
      console.error(`Error performing bulk ${operation}:`, error)
      enqueueSnackbar(`Error performing bulk ${operation}`, { variant: "error" })
    } finally {
      setLoading(false)
    }
  }

  const handleReprocessFailed = async () => {
    setLoading(true)
    try {
      const response = await reprocessFailedAiAgentKnowledgeBase(agentId)
      verifyResponse(response)
      enqueueSnackbar("Failed items reprocessing started", { variant: "success" })
      loadKnowledgeBase()
    } catch (error) {
      console.error("Error reprocessing failed items:", error)
      enqueueSnackbar("Error reprocessing failed items", { variant: "error" })
    } finally {
      setLoading(false)
    }
  }

  const handleSyncExternalSources = async () => {
    setLoading(true)
    try {
      const response = await syncExternalAiAgentKnowledgeBaseSources(agentId)
      verifyResponse(response)
      const syncedCount = response?.data?.dueSources ?? response?.dueSources ?? 0
      enqueueSnackbar(`External sync completed for ${syncedCount} source(s)`, { variant: "success" })
      loadKnowledgeBase()
    } catch (error) {
      console.error("Error syncing external knowledge sources:", error)
      const errorMessage = error.response?.data?.message || "Error syncing external knowledge sources"
      enqueueSnackbar(errorMessage, { variant: "error" })
    } finally {
      setLoading(false)
    }
  }

  const handleSyncExternalSource = async (item) => {
    setLoading(true)
    try {
      const response = await syncExternalAiAgentKnowledgeBaseSource(item.id)
      verifyResponse(response)
      const chunksCreated = response?.data?.chunksCreated ?? response?.chunksCreated ?? 0
      enqueueSnackbar(`External source synced (${chunksCreated} chunks)`, { variant: "success" })
      loadKnowledgeBase()
    } catch (error) {
      console.error("Error syncing external source:", error)
      const errorMessage = error.response?.data?.message || "Error syncing external source"
      enqueueSnackbar(errorMessage, { variant: "error" })
    } finally {
      setLoading(false)
    }
  }

  const handleConnectExternalSourceOAuth = async () => {
    setOauthConnecting(true)
    try {
      const response = await startAiAgentKnowledgeBaseOAuth({
        provider: newKnowledge.sourceProvider,
        returnOrigin: window.location.origin,
      })
      verifyResponse(response)
      const authorizeUrl =
        response?.data?.data?.authorizeUrl ||
        response?.data?.authorizeUrl ||
        response?.authorizeUrl
      if (!authorizeUrl) {
        throw new Error("OAuth authorize URL was not returned")
      }

      const popup = window.open(authorizeUrl, "rag-oauth", "width=620,height=760")
      if (!popup) {
        throw new Error("Popup blocked. Please allow popups and try again.")
      }

      const onMessage = async (event) => {
        let payload = event.data
        if (typeof payload === "string") {
          try {
            payload = JSON.parse(payload)
          } catch {
            return
          }
        }
        if (!payload?.type?.startsWith("admin-rag-oauth:")) return
        window.removeEventListener("message", onMessage)
        setOauthConnecting(false)

        if (payload.type === "admin-rag-oauth:error") {
          enqueueSnackbar(payload.message || "OAuth connection failed", { variant: "error" })
          return
        }

        const credential = payload.credential
        const credentialsResponse = await listAiAgentKnowledgeBaseOAuthCredentials(newKnowledge.sourceProvider)
        verifyResponse(credentialsResponse)
        const list = credentialsResponse?.data || credentialsResponse || []
        setOauthCredentials(Array.isArray(list) ? list : [])
        if (credential?.id) {
          setNewKnowledge((prev) => ({
            ...prev,
            credentialId: credential.id,
            credentialDisplayName: credential.displayName || "",
          }))
        }
        enqueueSnackbar("OAuth connection saved", { variant: "success" })
      }

      window.addEventListener("message", onMessage)
    } catch (error) {
      setOauthConnecting(false)
      enqueueSnackbar(error.message || "Unable to start OAuth connection", { variant: "error" })
    }
  }

  const handleSearch = async () => {
    setLoading(true)
    try {
      const searchParams = {
        page: 1,
        pageSize: 100,
        agentId: agentId,
        keyword: searchKeyword,
        contentType: searchContentType || undefined,
        processingStatus: searchStatus || undefined,
        sortField: "created_at",
        sortOrder: "DESC"
      }

      const response = await searchAiAgentKnowledgeBase(searchParams)
      verifyResponse(response)
      const list = response.data?.list || []
      setKnowledgeBase(list)
      onCountChange?.(list.length)
      enqueueSnackbar("Search completed", { variant: "success" })
    } catch (error) {
      console.error("Error searching knowledge base:", error)
      enqueueSnackbar("Error searching knowledge base", { variant: "error" })
    } finally {
      setLoading(false)
    }
  }

  const handleRagSearch = async () => {
    if (!ragSearchQuery.trim()) {
      enqueueSnackbar("Please enter a search query", { variant: "warning" })
      return
    }

    setLoading(true)
    try {
      const searchParams = {
        agentId: agentId,
        query: ragSearchQuery,
        limit: 5
      }

      const response = await ragSearchAiAgentKnowledgeBase(searchParams)
      verifyResponse(response)
      setRagSearchResults(response.results || [])
      enqueueSnackbar("RAG search completed", { variant: "success" })
    } catch (error) {
      console.error("Error performing RAG search:", error)
      enqueueSnackbar("Error performing RAG search", { variant: "error" })
    } finally {
      setLoading(false)
    }
  }

  // Cleanup object URLs on unmount
  useEffect(() => {
    return () => {
      if (selectedFile) {
        URL.revokeObjectURL(selectedFile)
      }
    }
  }, [selectedFile])

  const handleCloseDialog = () => {
    setAddKnowledgeDialog(false)
    resetForm()
  }



  const getFileIconByExtension = (fileName) => {
    const mediaInfo = findMediaInfoByFileName(fileName)
    switch (mediaInfo.type) {
      case "video":
        return <VideoIcon color="secondary" />
      case "audio":
        return <AudioIcon color="info" />
      case "image":
        return <ImageIcon color="success" />
      case "document":
        if (fileName.toLowerCase().includes('.pdf')) {
          return <PdfIcon color="error" />
        }
        return <DocIcon color="primary" />
      default:
        return <FileIcon color="action" />
    }
  }

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 Bytes"
    const k = 1024
    const sizes = ["Bytes", "KB", "MB", "GB"]
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i]
  }

  const getContentTypeIcon = (contentType) => {
    switch (contentType) {
      case "file":
        return "📄"
      case "text":
        return "📝"
      case "audio":
        return "🎵"
      case "image":
        return "🖼️"
      case "video":
        return "🎥"
      case "pdf":
        return "📕"
      case "document":
        return "📄"
      case "external_source":
        return "🔗"
      default:
        return "📄"
    }
  }

  const handleDownloadKnowledge = async (item) => {
    if (!item.id) {
      enqueueSnackbar("Cannot download: missing file ID", { variant: "error" })
      return
    }

    try {
      setLoading(true)
      await downloadAiAgentKnowledgeBaseFile(item.id, item.contentTitle)
      enqueueSnackbar("File downloaded successfully", { variant: "success" })
    } catch (error) {
      console.error("Error downloading file:", error)
      enqueueSnackbar("Error downloading file", { variant: "error" })
    } finally {
      setLoading(false)
    }
  }

  if (agentId === undefined || agentId === null) return null

  return (
    <>
      {/* Knowledge Base */}
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Knowledge Base ({knowledgeBase.length} items)
              </Typography>
              <Box className="knowledge-toolbar">
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleReprocessFailed}
                  disabled={loading}
                >
                  Reprocess Failed
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<SyncIcon />}
                  onClick={handleSyncExternalSources}
                  disabled={loading}
                >
                  Sync External Sources
                </Button>
                {access.AI_AGENT_KNOWLEDGE_BASE_ADD && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => setAddKnowledgeDialog(true)}
                  >
                    Add Knowledge Item
                  </Button>
                )}
              </Box>
            </Box>

            {/* Search Form */}
            <Box className="knowledge-search">
              <Typography variant="subtitle2" gutterBottom>
                Search Knowledge Base
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search by keyword..."
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Content Type</InputLabel>
                    <Select
                      value={searchContentType}
                      onChange={(e) => setSearchContentType(e.target.value)}
                      label="Content Type"
                    >
                                               <MenuItem value="">All Types</MenuItem>
                       <MenuItem value="text">Text</MenuItem>
                       <MenuItem value="audio">Audio</MenuItem>
                       <MenuItem value="image">Image</MenuItem>
                       <MenuItem value="video">Video</MenuItem>
                       <MenuItem value="pdf">PDF</MenuItem>
                       <MenuItem value="document">Document</MenuItem>
                       <MenuItem value="external_source">External Sync Source</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={searchStatus}
                      onChange={(e) => setSearchStatus(e.target.value)}
                      label="Status"
                    >
                      <MenuItem value="">All Statuses</MenuItem>
                      <MenuItem value="completed">Completed</MenuItem>
                      <MenuItem value="pending">Pending</MenuItem>
                      <MenuItem value="processing">Processing</MenuItem>
                      <MenuItem value="failed">Failed</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={1}>
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleSearch}
                    disabled={loading}
                  >
                    Search
                  </Button>
                </Grid>
                <Grid item xs={12} md={1}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={() => {
                      setSearchKeyword('')
                      setSearchContentType('')
                      setSearchStatus('')
                      loadKnowledgeBase()
                    }}
                    disabled={loading}
                  >
                    Clear
                  </Button>
                </Grid>
                <Grid item xs={12} md={1}>
                  <Button
                    fullWidth
                    variant="contained"
                    color="secondary"
                    onClick={() => setRagSearchDialog(true)}
                    disabled={loading}
                  >
                    RAG Search
                  </Button>
                </Grid>
              </Grid>
            </Box>

            {/* Bulk Operations */}
            {selectedItems.length > 0 && (
              <Box mb={2} p={2} bgcolor="action.hover" borderRadius={1}>
                <Typography variant="subtitle2" gutterBottom>
                  {selectedItems.length} item(s) selected
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => handleBulkOperation('delete')}
                    disabled={loading}
                  >
                    Delete Selected
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => handleBulkOperation('activate')}
                    disabled={loading}
                  >
                    Activate Selected
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="secondary"
                    onClick={() => handleBulkOperation('reprocess')}
                    disabled={loading}
                  >
                    Reprocess Selected
                  </Button>
                </Box>
              </Box>
            )}

            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <input
                        type="checkbox"
                        checked={selectedItems.length === knowledgeBase.length && knowledgeBase.length > 0}
                        onChange={handleSelectAll}
                        disabled={loading}
                      />
                    </TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>Content Text</TableCell>
                                           <TableCell>Size</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {knowledgeBase.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell padding="checkbox">
                        <input
                          type="checkbox"
                          checked={selectedItems.includes(item.id)}
                          onChange={() => handleSelectItem(item.id)}
                          disabled={loading}
                        />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center">
                          <span style={{ marginRight: "8px" }}>
                            {getContentTypeIcon(item.contentType)}
                          </span>
                          {item.contentType}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="subtitle2">{item.contentTitle}</Typography>
                      </TableCell>
                      <TableCell className="description-cell">
                        {item.contentDescription}
                      </TableCell>
                      <TableCell className="content-text-cell">
                        {item.contentType === "external_source" ? (
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>
                              {item.metadata?.ragSync?.providerLabel || item.metadata?.ragSync?.provider || "External source"}
                            </Typography>
                            <Typography variant="caption" color="textSecondary" display="block">
                              Sync: {item.metadata?.ragSync?.status || "configured"}
                            </Typography>
                            {item.metadata?.ragSync?.lastSyncedAt && (
                              <Typography variant="caption" color="textSecondary" display="block">
                                Last synced: {new Date(item.metadata.ragSync.lastSyncedAt).toLocaleString()}
                              </Typography>
                            )}
                            {item.metadata?.ragSync?.stats && (
                              <Typography variant="caption" color="textSecondary" display="block">
                                {item.metadata.ragSync.stats.scannedDocuments || 0} docs, {item.metadata.ragSync.stats.chunksCreated || 0} chunks
                              </Typography>
                            )}
                          </Box>
                        ) : item.contentText ? (
                          <Typography
                            variant="body2"
                            style={{
                              maxWidth: "300px",
                              maxHeight: "100px",
                              overflow: "auto",
                              whiteSpace: "pre-wrap",
                              fontSize: "0.75rem",
                              lineHeight: "1.2"
                            }}
                          >
                            {item.contentText}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
                            No text content
                          </Typography>
                        )}
                      </TableCell>
                                             <TableCell>
                       {item.contentType === "file" ? (
                         formatFileSize(item.fileSize)
                       ) : (
                         <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic" }}>
                           N/A
                         </Typography>
                       )}
                     </TableCell>
                      <TableCell>
                        <Chip
                          label={item.processingStatus}
                          size="small"
                          color={
                            item.processingStatus === "completed" ? "success" :
                            item.processingStatus === "failed" ? "error" :
                            item.processingStatus === "processing" ? "info" :
                            item.processingStatus === "pending" ? "warning" :
                            "default"
                          }
                        />
                      </TableCell>
                      <TableCell>
                        {new Date(item.createdAt).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="action-buttons">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewKnowledge(item)}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                          {item.contentType === "file" && (
                            <Tooltip title="Download File">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleDownloadKnowledge(item)}
                                disabled={loading}
                              >
                                <DownloadIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {item.contentType === "external_source" && (
                            <Tooltip title="Sync External Source">
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleSyncExternalSource(item)}
                                disabled={loading}
                              >
                                <SyncIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                          {access.AI_AGENT_KNOWLEDGE_BASE_DELETE && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => {
                                  setKnowledgeToDelete(item)
                                  setDeleteKnowledgeDialog(true)
                                }}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>

      {/* Add Knowledge Dialog */}
      <Dialog
        open={addKnowledgeDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 800, color: "#0f172a" }}>
          Add Knowledge Base Item
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Content Type Detection:</strong> The system will automatically determine the content type based on:
                </Typography>
                                 <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                   • <strong>File uploads:</strong> Type detected from file extension (PDF, image, video, audio, document)
                 </Typography>
                 <Typography variant="caption" display="block">
                   • <strong>Text input:</strong> Automatically set as "text" type
                 </Typography>
              </Alert>
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Knowledge Source</InputLabel>
                <Select
                  value={newKnowledge.sourceMode}
                  label="Knowledge Source"
                  onChange={(e) => {
                    if (selectedFile) {
                      URL.revokeObjectURL(selectedFile)
                    }
                    setSelectedFile(null)
                    setNewKnowledge({
                      ...newKnowledge,
                      sourceMode: e.target.value,
                      contentText: "",
                      fileSize: "",
                      mimeType: "",
                    })
                  }}
                >
                  <MenuItem value={KNOWLEDGE_SOURCE_MODES.MANUAL}>Upload or paste content</MenuItem>
                  <MenuItem value={KNOWLEDGE_SOURCE_MODES.EXTERNAL_SYNC}>
                    External sync source
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {newKnowledge.sourceMode === KNOWLEDGE_SOURCE_MODES.EXTERNAL_SYNC && (
              <>
                <Grid item xs={12}>
                  <Alert severity="info">
                    <Typography variant="body2">
                      Configure where the RAG ingestion service should scan for agent knowledge. OAuth credentials are stored securely by the backend and reused for future syncs across CMS logins.
                    </Typography>
                  </Alert>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Title"
                    value={newKnowledge.contentTitle}
                    onChange={(e) => setNewKnowledge({ ...newKnowledge, contentTitle: e.target.value })}
                    placeholder="e.g., NeuroGym shared Google Drive"
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Description"
                    value={newKnowledge.contentDescription}
                    onChange={(e) => setNewKnowledge({ ...newKnowledge, contentDescription: e.target.value })}
                    placeholder="What this source contributes to the agent's RAG dataset"
                    multiline
                    rows={2}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Provider</InputLabel>
                    <Select
                      value={newKnowledge.sourceProvider}
                      label="Provider"
                      onChange={(e) => setNewKnowledge({
                        ...newKnowledge,
                        sourceProvider: e.target.value,
                        credentialId: "",
                        credentialDisplayName: "",
                      })}
                    >
                      {EXTERNAL_SOURCE_PROVIDERS.map((provider) => (
                        <MenuItem key={provider.value} value={provider.value}>
                          {provider.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Sync Frequency</InputLabel>
                    <Select
                      value={newKnowledge.syncFrequency}
                      label="Sync Frequency"
                      onChange={(e) => setNewKnowledge({ ...newKnowledge, syncFrequency: e.target.value })}
                    >
                      {SYNC_FREQUENCY_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Folder, page, vault, or knowledge base URLs"
                    value={newKnowledge.sourceUrls}
                    onChange={(e) => setNewKnowledge({ ...newKnowledge, sourceUrls: e.target.value })}
                    placeholder="One URL or path per line"
                    helperText="Google Drive folders, Notion pages/databases, Obsidian vault paths, sitemap URLs, or other source roots."
                    multiline
                    minRows={3}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!newKnowledge.recursiveSync}
                        onChange={(e) => setNewKnowledge({ ...newKnowledge, recursiveSync: e.target.checked })}
                      />
                    }
                    label="Scan nested folders/pages"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!newKnowledge.realtimeSync}
                        onChange={(e) => setNewKnowledge({ ...newKnowledge, realtimeSync: e.target.checked })}
                      />
                    }
                    label="Use realtime change detection when available"
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Include Patterns"
                    value={newKnowledge.includePatterns}
                    onChange={(e) => setNewKnowledge({ ...newKnowledge, includePatterns: e.target.value })}
                    placeholder={"*.md\n*.pdf\ndocs/**"}
                    helperText="Optional. One glob or extension per line."
                    multiline
                    minRows={3}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Exclude Patterns"
                    value={newKnowledge.excludePatterns}
                    onChange={(e) => setNewKnowledge({ ...newKnowledge, excludePatterns: e.target.value })}
                    placeholder={"archive/**\ndrafts/**"}
                    helperText="Optional. One glob or extension per line."
                    multiline
                    minRows={3}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Box display="flex" gap={1} alignItems="flex-start">
                    <FormControl fullWidth size="small">
                      <InputLabel>Connected OAuth Account</InputLabel>
                      <Select
                        value={newKnowledge.credentialId}
                        label="Connected OAuth Account"
                        onChange={(e) => {
                          const selected = oauthCredentials.find((credential) => Number(credential.id) === Number(e.target.value))
                          setNewKnowledge({
                            ...newKnowledge,
                            credentialId: e.target.value,
                            credentialDisplayName: selected?.displayName || "",
                          })
                        }}
                      >
                        <MenuItem value="">No connected account selected</MenuItem>
                        {oauthCredentials.map((credential) => (
                          <MenuItem key={credential.id} value={credential.id}>
                            {credential.displayName || `${credential.provider} #${credential.id}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <Button
                      variant="outlined"
                      onClick={handleConnectExternalSourceOAuth}
                      disabled={oauthConnecting || newKnowledge.sourceProvider !== "google_drive"}
                      sx={{ whiteSpace: "nowrap", minHeight: 40 }}
                    >
                      {oauthConnecting ? "Connecting..." : "Connect Google Drive"}
                    </Button>
                  </Box>
                  <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.75 }}>
                    Use OAuth for Google Drive. The backend stores encrypted tokens and uses them for scheduled and manual syncs.
                  </Typography>
                </Grid>
              </>
            )}

            {newKnowledge.sourceMode !== KNOWLEDGE_SOURCE_MODES.EXTERNAL_SYNC && (
              <>
            {/* File Upload Section - Always show when adding knowledge */}
            <Grid item xs={12}>
              <Box
                className="upload-dropzone"
                sx={{
                  border: "2px dashed",
                  borderColor: dragActive ? "primary.main" : "grey.300",
                  borderRadius: 2,
                  p: 3,
                  textAlign: "center",
                  backgroundColor: dragActive ? "action.hover" : "background.paper",
                  transition: "all 0.2s ease",
                  cursor: "pointer",
                  "&:hover": {
                    borderColor: "primary.main",
                    backgroundColor: "action.hover",
                  },
                }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
                onClick={() => document.getElementById("file-input").click()}
              >
                  <input
                    id="file-input"
                    type="file"
                    style={{ display: "none" }}
                    onChange={handleFileInput}
                    accept={ACCEPT_FILE_EXT.map(ext => `.${ext}`).join(',')}
                  />

                  {!selectedFile ? (
                    <>
                      <CloudUploadIcon sx={{ fontSize: 48, color: "grey.400", mb: 2 }} />
                      <Typography variant="h6" gutterBottom>
                        Drop files here or click to browse
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        Supports PDF, Word, Markdown (.md), Video, Image, Audio files up to 100MB
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                        Files over 25MB will automatically use chunked upload for better reliability
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                        Content type will be automatically detected from file extension
                      </Typography>
                      <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                        <Chip label="Images" size="small" variant="outlined" />
                        <Chip label="Videos" size="small" variant="outlined" />
                        <Chip label="Audio" size="small" variant="outlined" />
                        <Chip label="Documents" size="small" variant="outlined" />
                        <Chip label="Markdown" size="small" variant="outlined" />
                      </Box>
                      <Box sx={{ mt: 2, textAlign: 'center' }}>
                        <Typography variant="caption" color="textSecondary" display="block">
                          Supported formats: {ACCEPT_FILE_EXT.slice(0, 8).join(', ')}
                          {ACCEPT_FILE_EXT.length > 8 && ` and ${ACCEPT_FILE_EXT.length - 8} more`}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                          Max file size: 100MB • Content type auto-detected
                        </Typography>
                      </Box>
                    </>
                  ) : (
                    <Box sx={{ textAlign: "left" }}>
                      <Box display="flex" alignItems="center" mb={2}>
                        {getFileIconByExtension(selectedFile.name)}
                        <Box ml={2}>
                          <Typography variant="subtitle1" noWrap>
                            {selectedFile.name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {formatFileSize(selectedFile.size)} • {selectedFile.type || "Unknown type"}
                          </Typography>
                          <Typography variant="caption" color="primary" display="block">
                            {(() => {
                              const ext = (selectedFile.name || "").split(".").pop()?.toLowerCase() || ""
                              if (ext === "md") return "text file"
                              return `${findMediaInfoByFileName(selectedFile.name).type} file`
                            })()}
                            {selectedFile.size >= 25 * 1024 * 1024 && (
                              <span style={{ color: '#ff9800', marginLeft: '8px' }}>
                                • Will use chunked upload
                              </span>
                            )}
                          </Typography>
                        </Box>
                      </Box>

                      {/* File Preview */}
                      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                        {(() => {
                          const mediaInfo = findMediaInfoByFileName(selectedFile.name)
                          switch (mediaInfo.type) {
                            case 'image':
                              return (
                                <Box
                                  sx={{
                                    width: 120,
                                    height: 120,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: '#f5f5f5'
                                  }}
                                >
                                  <img
                                    src={URL.createObjectURL(selectedFile)}
                                    alt="Preview"
                                    style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                                  />
                                </Box>
                              )
                            case 'video':
                              return (
                                <Box
                                  sx={{
                                    width: 120,
                                    height: 120,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    overflow: 'hidden',
                                    backgroundColor: '#f5f5f5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <video
                                    src={URL.createObjectURL(selectedFile)}
                                    style={{ maxWidth: '100%', maxHeight: '100%' }}
                                    controls
                                  />
                                </Box>
                              )
                            case 'audio':
                              return (
                                <Box
                                  sx={{
                                    width: 120,
                                    height: 80,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    backgroundColor: '#f5f5f5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <audio
                                    src={URL.createObjectURL(selectedFile)}
                                    controls
                                    style={{ width: '100%' }}
                                  />
                                </Box>
                              )
                            case 'document':
                              return (
                                <Box
                                  sx={{
                                    width: 120,
                                    height: 120,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    backgroundColor: '#f5f5f5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Box sx={{ textAlign: 'center' }}>
                                    {getFileIconByExtension(selectedFile.name)}
                                    <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                      {mediaInfo.ext.toUpperCase()}
                                    </Typography>
                                  </Box>
                                </Box>
                              )
                            default:
                              return (
                                <Box
                                  sx={{
                                    width: 120,
                                    height: 120,
                                    border: '1px solid #e0e0e0',
                                    borderRadius: 1,
                                    backgroundColor: '#f5f5f5',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <FileIcon sx={{ fontSize: 48, color: 'grey.500' }} />
                                </Box>
                              )
                          }
                        })()}
                      </Box>

                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          removeSelectedFile()
                        }}
                      >
                        Remove File
                      </Button>
                    </Box>
                  )}
                </Box>
              </Grid>

            {/* Content Type specific fields - Always show text field */}
            <Grid item xs={12}>
              <RichTextEditor
                value={newKnowledge.contentText}
                onChange={(value) => setNewKnowledge({ ...newKnowledge, contentText: value })}
                label="Content Text"
                placeholder="Enter your text content here... (optional if uploading a file)"
                toolbarConfig="description"
                minHeight="150px"
              />
            </Grid>

            {/* Common fields for all content types */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Title"
                value={newKnowledge.contentTitle}
                onChange={(e) => setNewKnowledge({ ...newKnowledge, contentTitle: e.target.value })}
                placeholder="Knowledge item title"
                required
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                label="Description"
                value={newKnowledge.contentDescription}
                onChange={(e) => setNewKnowledge({ ...newKnowledge, contentDescription: e.target.value })}
                placeholder="Brief description of the content"
                multiline
                rows={2}
              />
            </Grid>

            {/* File info fields - show when file is selected */}
            {selectedFile && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="MIME Type"
                    value={newKnowledge.mimeType}
                    onChange={(e) => setNewKnowledge({ ...newKnowledge, mimeType: e.target.value })}
                    placeholder="Auto-detected from file"
                    disabled
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="File Size"
                    value={formatFileSize(parseInt(newKnowledge.fileSize) || 0)}
                    disabled
                  />
                </Grid>
              </>
            )}

            {/* Manual MIME Type and File Size for non-file content */}
            {!selectedFile && (
              <>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="MIME Type"
                    value={newKnowledge.mimeType}
                    onChange={(e) => setNewKnowledge({ ...newKnowledge, mimeType: e.target.value })}
                    placeholder="e.g., text/plain, application/json"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="File Size (bytes)"
                    value={newKnowledge.fileSize}
                    onChange={(e) => setNewKnowledge({ ...newKnowledge, fileSize: e.target.value })}
                    placeholder="File size in bytes"
                    type="number"
                  />
                </Grid>
              </>
            )}
              </>
            )}
            </Grid>

          {/* Upload Progress */}
          {loading && uploadProgress > 0 && (
            <Box sx={{ mt: 2 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body2">
                  {isChunkedUpload ? `Uploading chunk ${currentChunk}/${totalChunks}...` : 'Uploading file...'}
                </Typography>
                <Typography variant="body2" color="primary">
                  {uploadProgress}%
                </Typography>
              </Box>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="textSecondary">
                  {isChunkedUpload
                    ? `Large file detected - uploading in ${totalChunks} chunks for better reliability...`
                    : 'Please wait while your file is being uploaded to the knowledge base...'
                  }
                </Typography>
                {canCancelUpload && (
                  <Button
                    size="small"
                    color="error"
                    onClick={cancelUpload}
                    sx={{ minWidth: 'auto', p: 0.5 }}
                  >
                    Cancel
                  </Button>
                )}
              </Box>
            </Box>
          )}

          {/* Validation Messages */}
          {newKnowledge.sourceMode !== KNOWLEDGE_SOURCE_MODES.EXTERNAL_SYNC && !selectedFile && !newKnowledge.contentText.trim() && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Please provide either a file or text content
            </Alert>
          )}

          {selectedFile && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>File Ready:</strong> {selectedFile.name}
              </Typography>
              <Typography variant="caption" display="block">
                • Size: {formatFileSize(selectedFile.size)} | Type: {selectedFile.type || 'Unknown'}
              </Typography>
              <Typography variant="caption" display="block">
                • Category: {(() => {
                  const ext = (selectedFile.name || "").split(".").pop()?.toLowerCase() || ""
                  return ext === "md" ? "text" : findMediaInfoByFileName(selectedFile.name).type
                })()}
              </Typography>
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={handleCloseDialog}
            disabled={loading}
            sx={dialogActionBtnSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddKnowledge}
            variant="contained"
            disabled={
              loading ||
              (newKnowledge.sourceMode === KNOWLEDGE_SOURCE_MODES.EXTERNAL_SYNC
                ? !newKnowledge.contentTitle.trim() ||
                  parseMultilineList(newKnowledge.sourceUrls).length === 0 ||
                  (newKnowledge.sourceProvider === "google_drive" && !newKnowledge.credentialId)
                : !selectedFile && !newKnowledge.contentText.trim())
            }
            sx={dialogActionBtnSx}
          >
            {loading ? "Adding..." : "Add Item"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Knowledge Dialog */}
      <Dialog
        open={deleteKnowledgeDialog}
        onClose={() => setDeleteKnowledgeDialog(false)}
        PaperProps={{
          sx: {
            ...dialogPaperSx,
            width: "min(520px, calc(100vw - 28px))",
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 800, color: "#0f172a" }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{knowledgeToDelete?.contentTitle}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setDeleteKnowledgeDialog(false)}
            disabled={loading}
            sx={dialogActionBtnSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteKnowledge}
            color="error"
            variant="contained"
            disabled={loading}
            sx={dialogActionBtnSx}
          >
            {loading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Knowledge Details Dialog */}
      <Dialog
        open={viewKnowledgeDialog}
        onClose={() => setViewKnowledgeDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 800, color: "#0f172a" }}>
          Knowledge Base Item Details
        </DialogTitle>
        <DialogContent>
          {knowledgeToView && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  {knowledgeToView.contentTitle}
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  {knowledgeToView.contentDescription}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Content Type
                </Typography>
                <Chip
                  label={knowledgeToView.contentType}
                  color="primary"
                  variant="outlined"
                  icon={getContentTypeIcon(knowledgeToView.contentType) === "📄" ? <FileIcon /> :
                        getContentTypeIcon(knowledgeToView.contentType) === "📝" ? <EditIcon /> :
                        <DownloadIcon />}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Status
                </Typography>
                                        <Chip
                          label={knowledgeToView.processingStatus}
                          size="small"
                          color={
                            knowledgeToView.processingStatus === "completed" ? "success" :
                            knowledgeToView.processingStatus === "failed" ? "error" :
                            knowledgeToView.processingStatus === "processing" ? "info" :
                            knowledgeToView.processingStatus === "pending" ? "warning" :
                            "default"
                          }
                        />
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  File Size
                </Typography>
                <Typography variant="body2">
                  {knowledgeToView.fileSize ? formatFileSize(knowledgeToView.fileSize) : "N/A"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  MIME Type
                </Typography>
                <Typography variant="body2">
                  {knowledgeToView.mimeType || "N/A"}
                </Typography>
              </Grid>



              {knowledgeToView.contentText && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Content Text
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
                    <Typography
                      variant="body2"
                      style={{
                        whiteSpace: "pre-wrap",
                        fontFamily: "'Courier New', monospace",
                        fontSize: "0.875rem",
                        lineHeight: "1.4",
                        maxHeight: "400px",
                        overflow: "auto"
                      }}
                    >
                      {knowledgeToView.contentText}
                    </Typography>
                  </Paper>
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Created
                </Typography>
                <Typography variant="body2">
                  {new Date(knowledgeToView.createdAt).toLocaleString()}
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" gutterBottom>
                  Updated
                </Typography>
                <Typography variant="body2">
                  {new Date(knowledgeToView.updatedAt).toLocaleString()}
                </Typography>
              </Grid>

              {knowledgeToView.metadata && Object.keys(knowledgeToView.metadata).length > 0 && (
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Metadata
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 2, backgroundColor: "#f5f5f5" }}>
                    <Grid container spacing={1}>
                      {Object.entries(knowledgeToView.metadata).map(([key, value]) => (
                        <Grid item xs={12} md={6} key={key}>
                          <Typography variant="caption" color="textSecondary" display="block">
                            {key}
                          </Typography>
                          <Typography variant="body2">
                            {Array.isArray(value) ? value.join(', ') : String(value)}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setViewKnowledgeDialog(false)}
            sx={dialogActionBtnSx}
          >
            Close
          </Button>
          {knowledgeToView?.contentType === "file" && (
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={() => {
                handleDownloadKnowledge(knowledgeToView)
                setViewKnowledgeDialog(false)
              }}
              sx={dialogActionBtnSx}
            >
              Download
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* RAG Search Dialog */}
      <Dialog
        open={ragSearchDialog}
        onClose={() => setRagSearchDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: dialogPaperSx }}
      >
        <DialogTitle sx={{ pb: 1, fontWeight: 800, color: "#0f172a" }}>
          RAG Search
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                placeholder="Enter your question or search query..."
                value={ragSearchQuery}
                onChange={(e) => setRagSearchQuery(e.target.value)}
                label="Search Query"
              />
            </Grid>

            {ragSearchResults.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Search Results
                </Typography>
                {ragSearchResults.map((result, index) => (
                  <Paper key={index} variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      {result.contentTitle || `Result ${index + 1}`}
                    </Typography>
                    <Typography variant="body2" color="textSecondary" paragraph>
                      {result.contentDescription || result.contentText || result.excerpt}
                    </Typography>
                    <Box display="flex" gap={1} alignItems="center">
                      <Chip
                        label={result.contentType}
                        size="small"
                        variant="outlined"
                        icon={getContentTypeIcon(result.contentType)}
                      />
                      {result.score && (
                        <Chip
                          label={`Score: ${(result.score * 100).toFixed(1)}%`}
                          size="small"
                          color="primary"
                        />
                      )}
                    </Box>
                  </Paper>
                ))}
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setRagSearchDialog(false)}
            sx={dialogActionBtnSx}
          >
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleRagSearch}
            disabled={loading || !ragSearchQuery.trim()}
            sx={dialogActionBtnSx}
          >
            Search
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default KnowledgeBasePanel
